import { createClient, type SupabaseClient } from "@supabase/supabase-js";

/**
 * Supabase browser client (RLS, behind Cloudflare Access).
 *
 * Backend-less architecture: the browser talks to Supabase directly using the
 * anon (publishable) key. RLS policies + CF Access enforce access control.
 * The service_role key MUST NEVER appear in client code.
 *
 * ENV GUARD: this module is imported transitively by UI pages and pure-module
 * tests, so it MUST NOT throw at import time when `.env` is absent (otherwise
 * `npm run build` / `npm run dev` break before any screen renders). The client
 * is created LAZILY: env is validated only when `getSupabase()` is first called.
 * `isSupabaseConfigured()` lets the UI degrade gracefully (read-only / banner)
 * instead of crashing.
 *
 * B3 builds typed data-access helpers (src/lib/db) on top of `getSupabase()`.
 */

function readEnv(): { url?: string; anonKey?: string } {
  // import.meta.env is replaced statically by Vite. Guard for non-Vite (tsx)
  // contexts where import.meta.env may be undefined.
  const env =
    (typeof import.meta !== "undefined" &&
      (import.meta as { env?: Record<string, string | undefined> }).env) ||
    {};
  return {
    url: env.VITE_SUPABASE_URL,
    anonKey: env.VITE_SUPABASE_ANON_KEY,
  };
}

/** True when both VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are present. */
export function isSupabaseConfigured(): boolean {
  const { url, anonKey } = readEnv();
  return Boolean(url && anonKey);
}

let client: SupabaseClient | null = null;

/**
 * Lazily create (and cache) the Supabase client. Throws ONLY when actually
 * invoked without env configured — never at module import time.
 */
export function getSupabase(): SupabaseClient {
  if (client) return client;
  const { url, anonKey } = readEnv();
  if (!url || !anonKey) {
    throw new Error(
      "[supabase] Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY. " +
        "Copy .env.example to .env and fill in the values. " +
        "(Use isSupabaseConfigured() to guard before calling getSupabase().)",
    );
  }
  client = createClient(url, anonKey, {
    auth: {
      // App sits behind Cloudflare Access SSO; we do not persist Supabase auth
      // sessions in the browser by default.
      persistSession: false,
      autoRefreshToken: false,
    },
  });
  return client;
}

export type { SupabaseClient };
